import { getBaseURL } from './apiClient';
import { getToken } from '../utils/jwtUtils';

export interface SessionPerformance {
  id: number;
  title: string;
  startDate: string;
  endDate: string;
  totalStudents: number;
  totalProblems: number;
  averageScore: number; // Percentage of problems solved correctly
  completionRate: number; // Percentage of problems attempted
}

export interface StudentPerformance {
  id: number;
  name: string;
  problemsSolved: number;
  totalAttempts: number;
  accuracy: number;
  sessionsParticipated: number;
}

export interface ProblemTypeDistribution {
  category: string;
  count: number;
  percentage: number;
}

export interface DifficultyDistribution {
  level: number;
  count: number;
  percentage: number;
}

// Get session performance metrics
export const getSessionPerformanceReport = async (): Promise<SessionPerformance[]> => {
  try {
    const response = await fetch(`${getBaseURL()}/api/reports/session-performance`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching session performance report:', error);
    return [];
  }
};

// Get top performing students
export const getTopStudentsReport = async (limit = 10): Promise<StudentPerformance[]> => {
  try {
    const response = await fetch(`${getBaseURL()}/api/reports/top-students?limit=${limit}`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching top students report:', error);
    return [];
  }
};

// Get problem type distribution
export const getProblemTypeDistribution = async (): Promise<ProblemTypeDistribution[]> => {
  try {
    const response = await fetch(`${getBaseURL()}/api/reports/problem-type-distribution`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching problem type distribution:', error);
    return [];
  }
};

// Get problem difficulty distribution
export const getDifficultyDistribution = async (): Promise<DifficultyDistribution[]> => {
  try {
    const response = await fetch(`${getBaseURL()}/api/reports/difficulty-distribution`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching difficulty distribution:', error);
    return [];
  }
};

// Get session comparison data
export const getSessionComparison = async (): Promise<SessionPerformance[]> => {
  try {
    // Get session performance data (same as session performance report)
    return await getSessionPerformanceReport();
  } catch (error) {
    console.error('Error fetching sessions for comparison:', error);
    return [];
  }
};

export interface ProblemSolvingRate {
  day: string;
  problemsSolved: number;
  studentsActive: number;
}

export interface AchievementData {
  name: string;
  count: number;
  percentage: number;
}

// Get daily problem solving rate
export const getDailyProblemSolvingRate = async (days = 7): Promise<ProblemSolvingRate[]> => {
  try {
    const response = await fetch(`${getBaseURL()}/api/reports/daily-problem-solving-rate?days=${days}`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching daily problem solving rate:', error);
    return [];
  }
};

// Get achievement distribution
export const getAchievementDistribution = async (): Promise<AchievementData[]> => {
  try {
    const response = await fetch(`${getBaseURL()}/api/reports/achievement-distribution`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching achievement distribution:', error);
    return [];
  }
};

